import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { empresasService, type Empresa } from '../../services/empresas';
import { getEmpresaActivaId } from '../../lib/empresaActiva';

/**
 * Con qué empresa se está trabajando, y cómo cambiar de una a otra.
 *
 * No se pinta nada cuando la cuenta tiene una sola empresa, que es el caso de todos los planes
 * menos PLUS: un desplegable con una única opción sólo estorba. En cuanto hay una segunda, el
 * nombre pasa a verse siempre en la barra superior — al cambiar de empresa cambia todo lo que se
 * mira debajo, y no saber en cuál estás es la forma de facturarle a la empresa equivocada.
 */
export default function SelectorDeEmpresa() {
  const [empresas, setEmpresas] = useState<Empresa[]>([]);
  const [abierto, setAbierto] = useState(false);
  const contenedor = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    empresasService
      .listar()
      .then((lista) => setEmpresas(lista.filter((e) => e.activa)))
      .catch((e) => console.warn('No se pudieron cargar las empresas:', e?.message ?? e));
  }, []);

  useEffect(() => {
    if (!abierto) return;
    const alPulsarFuera = (ev: MouseEvent) => {
      if (contenedor.current && !contenedor.current.contains(ev.target as Node)) setAbierto(false);
    };
    document.addEventListener('mousedown', alPulsarFuera);
    return () => document.removeEventListener('mousedown', alPulsarFuera);
  }, [abierto]);

  if (empresas.length < 2) return null;

  // La activa es la que quedó elegida, y si no hay ninguna elegida, la principal: es exactamente
  // el mismo orden de preferencia que sigue `resolveTenantId` al decidir a qué empresa consultar.
  const elegida = getEmpresaActivaId();
  const activa = empresas.find((e) => e.id === elegida) ?? empresas.find((e) => e.es_principal) ?? empresas[0];

  return (
    <div className="relative" ref={contenedor}>
      <button
        type="button"
        onClick={() => setAbierto((v) => !v)}
        className="flex items-center gap-x-2 rounded-lg border border-gray-200 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors duration-200"
        title="Cambiar de empresa"
      >
        <i className="ri-building-line text-gray-400"></i>
        <span className="max-w-[10rem] truncate">{activa?.nombre}</span>
        <i className={`ri-arrow-down-s-line text-gray-400 transition-transform ${abierto ? 'rotate-180' : ''}`}></i>
      </button>

      {abierto && (
        <div className="absolute right-0 top-full mt-2 w-72 rounded-xl border border-gray-100 bg-white shadow-xl ring-1 ring-black/5 z-50">
          <div className="px-4 py-2 text-xs font-semibold uppercase tracking-wide text-gray-400">
            Tus empresas
          </div>
          <ul className="max-h-72 overflow-y-auto py-1">
            {empresas.map((empresa) => {
              const esActiva = empresa.id === activa?.id;
              return (
                <li key={empresa.id}>
                  <button
                    type="button"
                    disabled={esActiva}
                    onClick={() => empresasService.cambiarA(empresa.id)}
                    className={`flex w-full items-center justify-between gap-x-3 px-4 py-2.5 text-left text-sm transition-colors duration-200 ${
                      esActiva ? 'bg-blue-50 text-blue-700' : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <span className="min-w-0">
                      <span className="block truncate font-medium">{empresa.nombre}</span>
                      {empresa.rnc && (
                        <span className="block truncate text-xs text-gray-400">RNC {empresa.rnc}</span>
                      )}
                    </span>
                    {esActiva && <i className="ri-check-line shrink-0"></i>}
                  </button>
                </li>
              );
            })}
          </ul>
          <div className="border-t border-gray-100 p-2">
            <Link
              to="/settings/empresas"
              onClick={() => setAbierto(false)}
              className="flex items-center gap-x-2 rounded-lg px-2 py-2 text-sm text-gray-600 hover:bg-gray-50"
            >
              <i className="ri-settings-3-line"></i>
              Gestionar empresas
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
